/**
 * Геометрия нижней панели: контур плиты с впадиной под выбранным разделом.
 *
 * Назначение файла: посчитать один SVG-путь для всей плиты. Впадина не собирается
 * из двух половинок с обратным радиусом, а решается как три касательные дуги:
 * выпуклое плечо уводит ровный край вниз, вогнутая чаша обнимает шарик,
 * второе плечо возвращает край наверх. Радиусы плеч независимы, поэтому впадина
 * умеет наклоняться на ходу: заднее плечо растягивается, переднее поджимается.
 *
 * Внешнее касание даёт половину ширины плеча из одной формулы: если у плеча
 * радиус s, у чаши rb, а центры на расстоянии s + rb, то
 * halfWidth = sqrt((s + rb)^2 - (s - bowlY)^2).
 *
 * Здесь нет ни React, ни DOM: только числа, поэтому поведение проверяется тестами.
 */

/** Оси: x вдоль панели, y вниз от ровного края плиты. */
export interface TroughInput {
  /** Ширина плиты. */
  readonly width: number;
  /** Высота плиты. */
  readonly height: number;
  /** Радиус скругления углов плиты. */
  readonly corner: number;
  /** Центр шарика по горизонтали. */
  readonly beadX: number;
  /** Радиус шарика. */
  readonly beadRadius: number;
  /**
   * Скорость шарика, приведённая к диапазону -1…1.
   * Знак — направление, ноль — покой. От неё зависит перекос впадины.
   */
  readonly velocity: number;
}

export interface TroughGeometry {
  /** Путь плиты для атрибута d. */
  readonly path: string;
  /** Полуширина впадины со стороны начала и конца оси. */
  readonly halfWidthStart: number;
  readonly halfWidthEnd: number;
  /** Растяжение шарика по ходу движения: радиусы эллипса. */
  readonly beadRx: number;
  readonly beadRy: number;
}

/** Насколько чаша шире шарика: зазор, чтобы край не касался шарика. */
const BOWL_GAP = 6;

/** Базовый радиус плеча относительно радиуса шарика. */
const SHOULDER_BASE = 1.35;

/** Насколько скорость растягивает заднее плечо и поджимает переднее. */
const SHOULDER_LEAN = 0.75;

/** Насколько скорость сплющивает шарик по ходу движения. */
const BEAD_SQUASH = 0.16;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Полуширина плеча из внешнего касания.
 * Если вертикальная разница центров больше суммы радиусов, касания нет — вернётся 0.
 */
export function shoulderHalfWidth(shoulder: number, bowl: number, bowlY: number): number {
  const centers = shoulder + bowl;
  const vertical = shoulder - bowlY;
  const squared = centers * centers - vertical * vertical;
  return squared > 0 ? Math.sqrt(squared) : 0;
}

/** Точка касания плеча и чаши: лежит на линии между их центрами. */
function tangencyPoint(
  shoulderX: number,
  shoulderY: number,
  shoulder: number,
  bowlX: number,
  bowlY: number,
  bowl: number,
): [number, number] {
  const dx = bowlX - shoulderX;
  const dy = bowlY - shoulderY;
  const distance = shoulder + bowl;
  return [shoulderX + (dx * shoulder) / distance, shoulderY + (dy * shoulder) / distance];
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Контур плиты с впадиной.
 *
 * Размер впадины ограничен не только скоростью: внешнее плечо обязано уместиться
 * между центром крайнего раздела и скруглением угла плиты. На узкой панели с пятью
 * разделами решает именно угол, поэтому плечи сжимаются до доступного места.
 */
export function troughPath(input: TroughInput): TroughGeometry {
  const { width, height, corner, beadRadius, velocity } = input;
  const beadX = clamp(input.beadX, corner, width - corner);
  const bowl = beadRadius + BOWL_GAP;
  const bowlY = 0;

  const lean = clamp(velocity, -1, 1);
  // Заднее плечо тянется следом за шариком, переднее поджимается.
  const shoulderStart = beadRadius * SHOULDER_BASE * (1 + SHOULDER_LEAN * lean);
  const shoulderEnd = beadRadius * SHOULDER_BASE * (1 - SHOULDER_LEAN * lean);

  const spaceStart = Math.max(0, beadX - corner);
  const spaceEnd = Math.max(0, width - corner - beadX);

  const fitShoulder = (shoulder: number, space: number): number => {
    let value = Math.max(beadRadius * 0.4, shoulder);
    // Плечо тем шире, чем больше его радиус: сжимаем, пока впадина не уместится.
    for (let i = 0; i < 24 && shoulderHalfWidth(value, bowl, bowlY) > space; i += 1) {
      value *= 0.85;
    }
    return value;
  };

  const sStart = fitShoulder(shoulderStart, spaceStart);
  const sEnd = fitShoulder(shoulderEnd, spaceEnd);

  const halfWidthStart = Math.min(shoulderHalfWidth(sStart, bowl, bowlY), spaceStart);
  const halfWidthEnd = Math.min(shoulderHalfWidth(sEnd, bowl, bowlY), spaceEnd);

  const startX = beadX - halfWidthStart;
  const endX = beadX + halfWidthEnd;

  const [tangentStartX, tangentStartY] = tangencyPoint(
    startX,
    sStart,
    sStart,
    beadX,
    bowlY,
    bowl,
  );
  const [tangentEndX, tangentEndY] = tangencyPoint(endX, sEnd, sEnd, beadX, bowlY, bowl);

  const path = [
    `M ${round(corner)} 0`,
    `L ${round(startX)} 0`,
    // Плечо вниз: центр под краем, дуга по часовой стрелке.
    `A ${round(sStart)} ${round(sStart)} 0 0 1 ${round(tangentStartX)} ${round(tangentStartY)}`,
    // Чаша под шариком: против часовой стрелки, поэтому край обнимает шарик снизу.
    `A ${round(bowl)} ${round(bowl)} 0 0 0 ${round(tangentEndX)} ${round(tangentEndY)}`,
    `A ${round(sEnd)} ${round(sEnd)} 0 0 1 ${round(endX)} 0`,
    `L ${round(width - corner)} 0`,
    `A ${round(corner)} ${round(corner)} 0 0 1 ${round(width)} ${round(corner)}`,
    `L ${round(width)} ${round(height - corner)}`,
    `A ${round(corner)} ${round(corner)} 0 0 1 ${round(width - corner)} ${round(height)}`,
    `L ${round(corner)} ${round(height)}`,
    `A ${round(corner)} ${round(corner)} 0 0 1 0 ${round(height - corner)}`,
    `L 0 ${round(corner)}`,
    `A ${round(corner)} ${round(corner)} 0 0 1 ${round(corner)} 0`,
    "Z",
  ].join(" ");

  const squash = 1 + BEAD_SQUASH * Math.abs(lean);

  return {
    path,
    halfWidthStart,
    halfWidthEnd,
    beadRx: beadRadius * squash,
    beadRy: beadRadius / squash,
  };
}
